import React, {
  MouseEvent,
  TouchEvent,
  useEffect,
  useState,
  useRef,
} from "react"
import styled, { css } from "styled-components"
import { Slide } from "./slider-item.component"
import SliderItem from "./slider-item.component"
import { AppFunctionComponent, ListItemBuilder } from "../../types"
import { ScrollAnchor } from "./sections/custom/innovation-lab-form/innovation-lab.component"

const sliderLayoutBreakpoint = 440

const SliderWrapper = styled.div`
  padding: 0;
  overflow: hidden;
  position: relative;
`

const Slider = styled.div`
  margin: 0 auto;
  max-width: 1260px;
  width: calc(100% - 30px);

  @media (min-width: 768px) {
    width: calc(100% - 60px);
  }
`

const slidesList = []

const SliderTrack = styled.div<{ currentSlideIndex: number }>`
  width: 100%;
  display: flex;
  user-select: none;
  flex-direction: row;
  transition: transform 0.5s ease-in-out;

  ${({ currentSlideIndex }) => css`
    --position-Y: ${currentSlideIndex}px;
    --position-X: calc(
      -1 * ${currentSlideIndex * 201}px + -1 * ${currentSlideIndex * 24}px
    );

    ${currentSlideIndex === 0 &&
    css`
      --position-X: 0px;
    `};

    ${currentSlideIndex === slidesList.length - 1 &&
    css`
      --position-X: calc(-${currentSlideIndex * 201}px);
    `};

    @media (min-width: 768px) {
      --position-X: calc(
        -1 * ${currentSlideIndex * 271}px + -1 * ${currentSlideIndex * 48}px
      );

      ${currentSlideIndex === slidesList.length - 1 &&
      css`
        --position-X: calc(-${currentSlideIndex * 271 + 48}px);
      `};
    }
  `};
  transform: translate3d(var(--position-X), 0, var(--position-Y));
`

const SlideItem = styled(SliderItem)<{ style?: ListItemBuilder["style"] }>`
  cursor: grab;
  min-width: 201px;
  max-width: 201px;
  user-select: none;

  &:not(:last-of-type) {
    margin-right: 24px;
  }

  @media (min-width: 768px) {
    min-width: 271px;
    max-width: 271px;
    user-select: none;

    &:not(:last-of-type) {
      margin-right: 48px;
    }
  }

  ${({ style }) =>
    style?.text_align === "center" &&
    css`
      text-align: center;
    `}

  div {
    box-shadow: none;
  }
`

const useMediaQuery = (breakpoint: number, func: () => void) => {
  useEffect(() => {
    const onResize = () => {
      func()
    }
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`)
    mq.addListener(onResize)
    return () => {
      mq.removeListener(onResize)
    }
  }, [func])
}

interface Props {
  slides?: Record<string, any>[]
  className?: string
  anchorId?: string
}

const SliderCustomImages: AppFunctionComponent<Props> = ({
  className,
  slides = [],
  anchorId,
}) => {
  const windowAvailable = typeof window !== `undefined`
  const calculateLastPossibleSlideIndex = () => {
    let lastIndex = slides.length - 1
    if (windowAvailable) {
      if (window.innerWidth >= sliderLayoutBreakpoint) {
        lastIndex--
      }
    }
    return lastIndex
  }

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [lastPossibleSlideIndex, setLastPossibleSlideIndex] = useState(
    calculateLastPossibleSlideIndex()
  )

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      setCurrentSlideIndex((prevIndex) => {
        const lastSlideIndex = calculateLastPossibleSlideIndex()
        if (prevIndex >= lastSlideIndex) {
          return 0
        } else {
          return prevIndex + 1
        }
      })
    }, 2500)
  }

  if (windowAvailable) {
    useMediaQuery(sliderLayoutBreakpoint, () => {
      setLastPossibleSlideIndex(calculateLastPossibleSlideIndex())
    })
  }

  useEffect(() => {
    if (!windowAvailable || slides.length === 0) return
    resetInterval()
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [slides.length, lastPossibleSlideIndex])

  const slide = (i: number) => {
    setCurrentSlideIndex(i)
  }

  const prev = () => {
    let newIndex =
      currentSlideIndex > 0 ? currentSlideIndex - 1 : currentSlideIndex
    if (
      slides.length - 1 === currentSlideIndex &&
      window.innerWidth >= sliderLayoutBreakpoint
    ) {
      newIndex = newIndex - 1
    }
    slide(newIndex)
    resetInterval()
  }

  const next = () => {
    const lastSlideIndex = calculateLastPossibleSlideIndex()
    const newIndex =
      currentSlideIndex < lastSlideIndex
        ? currentSlideIndex + 1
        : currentSlideIndex

    setLastPossibleSlideIndex(lastSlideIndex)
    slide(newIndex)
    resetInterval()
  }

  let touchStartX = 0
  let touchStartY = 0

  const touchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX = e.touches[0].clientX
    touchStartY = e.touches[0].clientY
  }

  const touchMove = (e: TouchEvent<HTMLDivElement>) => {
    const touchEndX = e.touches[0].clientX
    const touchEndY = e.touches[0].clientY

    const diffX = Math.abs(touchStartX - touchEndX)
    const diffY = Math.abs(touchStartY - touchEndY)

    if (diffX > 40 && diffY < 30) {
      touchEndX - touchStartX > 0 ? prev() : next()
    }
  }

  let mouseStartX = 0
  let mouseEndX = 0

  const mouseDown = (e: MouseEvent<HTMLDivElement>) => {
    mouseStartX = e.clientX
  }

  const mouseUp = (e: MouseEvent<HTMLDivElement>) => {
    mouseEndX = e.clientX

    const diffX = Math.abs(mouseStartX - mouseEndX)

    if (diffX > 100) {
      mouseEndX - mouseStartX > 0 ? prev() : next()
    }
  }

  return (
    <SliderWrapper
      className={className}
      onMouseDown={mouseDown}
      onMouseUp={mouseUp}
      onTouchStart={touchStart}
      onTouchMove={touchMove}
    >
      <ScrollAnchor id={anchorId} />
      <Slider>
        <SliderTrack currentSlideIndex={currentSlideIndex}>
          {slides.map((slideItem: Slide, index: number) => (
            <SlideItem slide={slideItem} key={index} style={slideItem.style} />
          ))}
        </SliderTrack>
      </Slider>
    </SliderWrapper>
  )
}

export default SliderCustomImages
